import Modifier from 'ember-modifier';
import tippy, { hideAll } from 'tippy.js';
import { inject as service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import { tracked } from '@glimmer/tracking';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { task, timeout } from 'ember-concurrency';

export default class HintOnAudioBlocked extends Modifier {
  @service stereo;

  @tracked identifier;
  @tracked blockedSound;
  @tracked blockedPrompt;

  constructor() {
    super(...arguments);
    // Bind once and reuse the same refs for on/off — re-binding (or invoking)
    // in unregister would never match, leaking the listener.
    this._onAudioBlocked = this.onAudioBlocked.bind(this);
    this._clearBlockedPrompt = this.clearBlockedPrompt.bind(this);
    registerDestructor(this, this.unregisterListeners.bind(this));
    this.stereo.on('audio-blocked', this._onAudioBlocked);
    this.stereo.on('audio-played', this._clearBlockedPrompt);
  }

  modify(element, [identifier], namedArgs = {}) {
    if (!this.element) {
      this.element = element;
    }

    this.identifier = normalizeIdentifier(identifier);
    if (this.element._tippy) {
      this.element._tippy.destroy();
    }
  }

  clearBlockedPrompt({ sound }) {
    if (sound.hasUrl(this.identifier)) {
      this.blockedSound = undefined;
      this.blockedPrompt?.hide();
    }
  }

  onAudioBlocked({ sound }) {
    if (sound.hasUrl(this.identifier)) {
      this.blockedSound = sound;
      this.handleAudioBlocked.perform(sound);
    }
  }

  handleAudioBlocked = task({ restartable: true }, async sound => {
    let instances = tippy([this.element], {
      role: 'tooltip',
      theme: 'attention',
      placement: 'bottom',
      maxWidth: '300px',
      hideOnClick: true,
      interactive: false,
      content:
        'We attempted to autoplay this for you, but your browser prevented it',
      trigger: 'manual',
      allowHTML: true,
    });

    this.blockedPrompt = instances[0];
    this.blockedPrompt?.show();
    await timeout(3000);

    this.blockedPrompt?.hide();
  });

  unregisterListeners() {
    this.stereo.off('audio-blocked', this._onAudioBlocked);
    this.stereo.off('audio-played', this._clearBlockedPrompt);

    if (this.element?._tippy) {
      this.element._tippy.destroy();
    }
  }
}
